import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, CalendarDays, Clock, Users, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { selfStudyOrder, getAvailability, membershipSubscribe } from "@/lib/api";
import { checkoutOrder, checkoutSubscription } from "@/lib/razorpay";

// ---- helpers ----
const pad = (n) => String(n).padStart(2, "0");
const toValue = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
const toLabel = (min) => {
  let h = Math.floor(min / 60), m = min % 60;
  const s = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${pad(m)} ${s}`;
};
const timeOptions = (open, close) => {
  const arr = [];
  for (let t = open; t <= close; t += 30) arr.push({ value: toValue(t), label: toLabel(t), min: t });
  return arr;
};

const Confirmation = ({ reference, lines, onClose }) => (
  <div className="py-4 text-center" data-testid="booking-confirmation">
    <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
    <h3 className="mt-4 font-display text-2xl font-semibold">Booking confirmed!</h3>
    <p className="mt-1 text-sm text-muted-foreground">Your payment was successful. Here are your details.</p>
    <div className="mt-5 rounded-2xl border border-border bg-secondary/40 p-5 text-left">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Booking reference</span>
        <span className="font-mono font-semibold" data-testid="booking-reference">{reference}</span>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="mt-2 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{l.k}</span><span className="font-medium">{l.v}</span>
        </div>
      ))}
    </div>
    <p className="mt-4 text-xs text-muted-foreground">A confirmation has been recorded. If email is set up, a receipt is on its way.</p>
    <Button onClick={onClose} className="mt-5 w-full rounded-full" data-testid="booking-confirmation-close">Done</Button>
  </div>
);

const Contact = ({ form, setForm }) => (
  <div className="grid gap-4 sm:grid-cols-3">
    <div className="space-y-2 sm:col-span-1">
      <Label htmlFor="bk-name">Name</Label>
      <Input id="bk-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="flow-name-input" />
    </div>
    <div className="space-y-2 sm:col-span-1">
      <Label htmlFor="bk-email">Email</Label>
      <Input id="bk-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="flow-email-input" />
    </div>
    <div className="space-y-2 sm:col-span-1">
      <Label htmlFor="bk-phone">Phone</Label>
      <Input id="bk-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional" data-testid="flow-phone-input" />
    </div>
  </div>
);

// =========================================================================
// SELF-STUDY
// =========================================================================
export const SelfStudyDialog = ({ open, setOpen, settings }) => {
  const price = settings?.self_study?.effective ?? 49;
  const hours = settings?.studio_hours || { open: 600, close: 1320 };
  const opts = timeOptions(hours.open, hours.close - 60);

  const [date, setDate] = useState(null);
  const [start, setStart] = useState("");
  const [numHours, setNumHours] = useState("1");
  const [people, setPeople] = useState("1");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (date) getAvailability(format(date, "yyyy-MM-dd")).then((d) => setBooked(d.booked || [])).catch(() => setBooked([]));
  }, [date]);

  useEffect(() => { if (!open) { setDone(null); setDate(null); setStart(""); setNumHours("1"); setPeople("1"); } }, [open]);

  const total = Number(people) * Number(numHours) * price;
  const canPay = date && start && Number(people) >= 1 && Number(numHours) >= 1 && form.name && form.email;

  const pay = async (e) => {
    e.preventDefault();
    if (!canPay) return;
    setLoading(true);
    try {
      const order = await selfStudyOrder({
        date: format(date, "yyyy-MM-dd"), start_time: start,
        hours: Number(numHours), people: Number(people), ...form,
      });
      if (!order.payments_enabled) { toast.success("Request saved! Payments aren't live yet — we'll follow up."); setOpen(false); return; }
      const res = await checkoutOrder(order, form);
      if (res.status === "paid") {
        const b = res.data?.booking;
        setDone({ reference: order.booking_reference, lines: [
          { k: "Date", v: format(date, "EEE d MMM yyyy") },
          { k: "Time", v: `${b?.start_label || toLabel(Number(start.split(":")[0])*60+Number(start.split(":")[1]))} – ${b?.end_label || ""}` },
          { k: "People × Hours", v: `${people} × ${numHours}` },
          { k: "Amount paid", v: `₹${total}` },
        ]});
      } else if (res.status === "cancelled") toast.info("Payment cancelled — your slot wasn't booked.");
      else toast.error(res.message || "Payment failed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="self-study-dialog">
        {done ? <Confirmation {...done} onClose={() => setOpen(false)} /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Book a Self-Study Group</DialogTitle>
              <DialogDescription>
                <span className="inline-flex items-center gap-2">
                  <Badge className="rounded-full bg-accent text-accent-foreground">Special Launch Price</Badge>
                  <span>₹{price}/person/hour <span className="line-through opacity-60">₹{settings?.self_study?.regular ?? 99}</span></span>
                </span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={pay} className="space-y-4">
              <div>
                <Label className="mb-2 block">Choose a date</Label>
                <div className="rounded-2xl border border-border p-2">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} data-testid="self-study-calendar" />
                </div>
              </div>
              {booked.length > 0 && (
                <p className="text-xs text-muted-foreground">Already booked that day: {booked.map((b) => `${b.start_label}–${b.end_label}`).join(", ")}</p>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Start</Label>
                  <Select value={start} onValueChange={setStart}>
                    <SelectTrigger data-testid="self-study-start"><SelectValue placeholder="Time" /></SelectTrigger>
                    <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hours</Label>
                  <Select value={numHours} onValueChange={setNumHours}>
                    <SelectTrigger data-testid="self-study-hours"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5,6].map((h) => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>People</Label>
                  <Select value={people} onValueChange={setPeople}>
                    <SelectTrigger data-testid="self-study-people"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({length:20},(_,i)=>i+1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Contact form={form} setForm={setForm} />
              <div className="rounded-2xl bg-secondary/50 p-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{people} people × {numHours} hours × ₹{price}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="font-display text-lg font-semibold">Total</span>
                  <span className="font-display text-2xl font-semibold text-primary" data-testid="self-study-total">₹{total}</span>
                </div>
              </div>
              <Button type="submit" disabled={!canPay || loading} className="w-full rounded-full" size="lg" data-testid="self-study-pay-button">
                {loading ? "Please wait…" : `Proceed to Payment · ₹${total}`}
              </Button>
              <p className="text-center text-xs text-muted-foreground">Subject to availability. Secure payment via Razorpay.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// MEMBERSHIP
// =========================================================================
export const MembershipDialog = ({ open, setOpen, settings }) => {
  const m = settings?.membership || { effective: 499, regular: 999, benefits: [] };
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { if (!open) setDone(null); }, [open]);

  const join = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      const sub = await membershipSubscribe(form);
      if (!sub.payments_enabled) { toast.success("Interest saved! Payments aren't live yet — we'll follow up."); setOpen(false); return; }
      if (sub.subscription_available === false) { toast.success(sub.message || "Interest saved — we'll set up your membership manually."); setOpen(false); return; }
      const res = await checkoutSubscription(sub, form);
      if (res.status === "active") {
        setDone({ reference: sub.membership_ref, lines: [
          { k: "Plan", v: "YTCA Membership Pass" },
          { k: "Billing", v: `₹${m.effective} / month` },
        ]});
      } else if (res.status === "cancelled") toast.info("Membership sign-up cancelled.");
      else toast.error(res.message || "Could not start membership.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="membership-dialog">
        {done ? (
          <div className="py-4 text-center" data-testid="membership-confirmation">
            <Sparkles className="mx-auto h-14 w-14 text-primary" />
            <h3 className="mt-4 font-display text-2xl font-semibold">Welcome to YTCA!</h3>
            <p className="mt-1 text-sm text-muted-foreground">Your membership is active.</p>
            <div className="mt-5 rounded-2xl border border-border bg-secondary/40 p-5 text-left">
              <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Membership ref</span><span className="font-mono font-semibold">{done.reference}</span></div>
              {done.lines.map((l, i) => <div key={i} className="mt-2 flex items-center justify-between text-sm"><span className="text-muted-foreground">{l.k}</span><span className="font-medium">{l.v}</span></div>)}
            </div>
            <Button onClick={() => setOpen(false)} className="mt-5 w-full rounded-full">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">YTCA Membership Pass</DialogTitle>
              <DialogDescription>
                <span className="inline-flex items-center gap-2">
                  <Badge className="rounded-full bg-accent text-accent-foreground">Special Launch Price</Badge>
                  <span className="font-semibold text-foreground">₹{m.effective} / month</span>
                  <span className="line-through opacity-60">₹{m.regular}/mo</span>
                </span>
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-2">
              {(m.benefits || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{b}</li>
              ))}
            </ul>
            <form onSubmit={join} className="mt-2 space-y-4">
              <Contact form={form} setForm={setForm} />
              <Button type="submit" disabled={loading} className="w-full rounded-full" size="lg" data-testid="membership-join-button">
                {loading ? "Please wait…" : `Join Membership · ₹${m.effective}/month`}
              </Button>
              <p className="text-center text-xs text-muted-foreground">Billed monthly via Razorpay. Cancel anytime. This is a recurring subscription, not a one-time payment.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
